/**
 * /casefiles and /view/:id — the two DB-driven iframe routes.
 *
 * Both were getServerSideProps pages: the server resolved the tenant from the request,
 * looked the record up in `agenthub_master_menu`, and passed the URL down as props. Each
 * ALSO carried a client-resolved branch for the SIYA iOS app, whose framed WKWebView
 * blocks third-party cookies so no credential ever reached the server render.
 *
 * With no server render there is one path, and it is that one — /api/embedded-page with
 * the token as a Bearer header. That is a simplification, not a loss: the fallback was
 * already the only path that worked on iOS, and it is the same query against the same
 * collection. The two pages also collapse into one component here, since the sole
 * difference between them was which href they looked up.
 *
 * What DOES go away is the server's `Cache-Control: private, no-store` on the HTML. It is
 * not needed any more and is not an omission to fix: the tenant-specific part is now a
 * JSON response from /api/embedded-page, which sets its own no-store, and the HTML shell
 * is identical for every tenant. The risk that header existed for — a CDN serving tenant
 * A's iframe URL to tenant B on the same /view/<id> path — is gone with the props.
 */

import { useParams } from 'react-router-dom';
import { EmptyState } from '@syia-ai/maritimeai-ui';
import SimpleLayout from '@/components/SimpleLayout';
import AppLoader from '@/components/AppLoader';
import { LinkButton } from '@/components/ui';
import OptimizedIframe from '@/components/OptimizedIframe';
import { useEmbeddedPage } from '@/hooks/useEmbeddedPage';

function Failed({ title, error }: { title: string; error: string }) {
  return (
    <SimpleLayout title={title}>
      <EmptyState
        className="shell-page-state"
        headingLevel={2}
        title="This page can't be shown"
        description={error}
        actions={<LinkButton href="/">Go to Home</LinkButton>}
      />
    </SimpleLayout>
  );
}

function EmbeddedPageRoute({ href, loadingTitle }: { href: string; loadingTitle: string }) {
  const state = useEmbeddedPage(href, true);

  if (state.status === 'error') return <Failed title="Error" error={state.error} />;

  if (state.status !== 'ready') {
    return (
      <SimpleLayout title={loadingTitle}>
        <AppLoader fill="area" />
      </SimpleLayout>
    );
  }

  const { label, url, embedCode, type } = state.page;

  if (!label || (!url && !embedCode)) {
    return <Failed title="Error" error="Page not found" />;
  }

  return (
    <SimpleLayout title={label}>
      <div className="shell-embedded-page">
        <OptimizedIframe url={url || ''} title={label} embedCode={embedCode} type={type} />
      </div>
    </SimpleLayout>
  );
}

export function CasefilesRoute() {
  return <EmbeddedPageRoute href="/casefiles" loadingTitle="Casefiles" />;
}

export function ViewRoute() {
  const { id } = useParams<{ id: string }>();
  return <EmbeddedPageRoute href={`/view/${id ?? ''}`} loadingTitle="Loading" />;
}
